import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Nav, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getError } from '../utils';
import casillaService from '../services/casilla.service';
import rifaService from '../services/rifa.service';
import { isSharedBoard, toSharedGridView } from '../lib/boardPlans';
import './BoardCasillaRepositionModal.css';

function mergeBoardCategoriesForDisplay(categories) {
  const m = new Map();
  for (const cat of categories || []) {
    const name = String(cat?.nombre ?? '').trim();
    if (!name) continue;
    const filas = Math.max(1, Number(cat.filas) || 1);
    const columnas = Math.max(1, Number(cat.columnas) || 1);
    const prev = m.get(name);
    if (!prev) {
      m.set(name, { ...cat, nombre: name, filas, columnas });
    } else {
      m.set(name, {
        ...prev,
        nombre: name,
        filas: Math.max(prev.filas, filas),
        columnas: Math.max(prev.columnas, columnas),
      });
    }
  }
  return [...m.values()];
}

function buildGrid(categoryName, filas, columnas, allCasillas) {
  const nameKey = String(categoryName ?? '').trim();
  const cats = allCasillas.filter(c => String(c.categoria ?? '').trim() === nameKey);
  const grid = [];
  for (let r = 0; r < filas; r++) {
    const row = [];
    for (let col = 0; col < columnas; col++) {
      const casilla = cats.find(c => Number(c.fila) === r && Number(c.columna) === col);
      row.push(casilla || null);
    }
    grid.push(row);
  }
  return grid;
}

function RepositionCell({
  casilla,
  cat,
  draggingId,
  setDraggingId,
  dropTargetId,
  setDropTargetId,
  onSwap,
  estadoLabel,
  t,
}) {
  if (!casilla) {
    return (
      <div
        className='reposition-cell reposition-cell--empty'
        style={{ backgroundColor: cat.color || '#adb5bd' }}
        title={t('reservation.repositionNoCasilla') || 'No square — expand board first'}
      >
        —
      </div>
    );
  }

  const sold = casilla.estado === 'Vendido';
  const reserved = casilla.estado === 'Reservado';
  const avail = casilla.estado === 'Disponible';

  return (
    <div
      className={`reposition-cell ${draggingId === casilla._id ? 'reposition-cell--dragging' : ''} ${
        dropTargetId === casilla._id ? 'reposition-cell--drop-target' : ''
      }`}
      style={{
        backgroundColor: sold ? '#6c757d' : reserved ? '#fd7e14' : cat.color || '#20c997',
      }}
      draggable
      title={`${estadoLabel} · ${casilla.nombreComprador || '—'}`}
      onDragStart={e => {
        setDraggingId(casilla._id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/casilla-id', String(casilla._id));
      }}
      onDragEnd={() => {
        setDraggingId(null);
        setDropTargetId(null);
      }}
      onDragOver={e => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropTargetId(casilla._id);
      }}
      onDragLeave={() => {
        setDropTargetId(prev => (prev === casilla._id ? null : prev));
      }}
      onDrop={e => {
        e.preventDefault();
        const fromId = e.dataTransfer.getData('text/casilla-id');
        setDropTargetId(null);
        setDraggingId(null);
        if (fromId && String(fromId) !== String(casilla._id)) {
          onSwap(fromId, casilla._id);
        }
      }}
    >
      <span>
        {sold ? 'V' : reserved ? 'R' : avail ? '·' : '?'}
        {casilla.nombreComprador ? (
          <span className='d-block text-truncate' style={{ maxWidth: '100%' }}>
            {String(casilla.nombreComprador).slice(0, 12)}
            {String(casilla.nombreComprador).length > 12 ? '…' : ''}
          </span>
        ) : null}
      </span>
    </div>
  );
}

export default function BoardCasillaRepositionModal({ show, onHide, rifaId, onAfterSwap }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [casillas, setCasillas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('');
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [swapBusy, setSwapBusy] = useState(false);

  const load = useCallback(
    async (opts = {}) => {
      const { withFullSpinner = true } = opts;
      if (!rifaId) return;
      try {
        if (withFullSpinner) setLoading(true);
        const [rifaRaw, casData] = await Promise.all([
          rifaService.getRifa(rifaId),
          casillaService.getCasillasByRifa(rifaId),
        ]);
        const rifa = rifaRaw?.data ?? rifaRaw;
        const list = Array.isArray(casData) ? casData : [];
        // Shared board: a single grid shown as one pseudo category
        const sharedView = isSharedBoard(rifa)
          ? toSharedGridView(rifa, list, t('raffle.sharedBoardLabel', 'Tablero'))
          : null;
        const merged = sharedView
          ? sharedView.categories
          : mergeBoardCategoriesForDisplay(rifa?.config?.categories || []);
        setCategories(merged);
        setCasillas(sharedView ? sharedView.casillas : list);
        setActiveCat(prev => {
          if (!merged.length) return '';
          const still = merged.some(c => c.nombre === prev);
          return still ? prev : merged[0].nombre;
        });
      } catch (err) {
        toast.error(getError(err));
      } finally {
        if (withFullSpinner) setLoading(false);
      }
    },
    [rifaId, t]
  );

  useEffect(() => {
    if (show && rifaId) {
      load();
    }
  }, [show, rifaId, load]);

  useEffect(() => {
    if (!show) {
      setDraggingId(null);
      setDropTargetId(null);
      setActiveCat('');
    }
  }, [show]);

  const onSwap = async (idA, idB) => {
    if (swapBusy) return;
    try {
      setSwapBusy(true);
      await casillaService.swapCasillas(idA, idB);
      toast.success(t('reservation.repositionSwapOk') || 'Positions updated');
      await load({ withFullSpinner: false });
      onAfterSwap?.();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSwapBusy(false);
    }
  };

  const currentCat = categories.find(c => c.nombre === activeCat);
  const grid = currentCat
    ? buildGrid(currentCat.nombre, currentCat.filas, currentCat.columnas, casillas)
    : [];

  const estadoLabel = e =>
    e === 'Vendido'
      ? t('board.sold') || 'Sold'
      : e === 'Reservado'
        ? t('board.reserved') || 'Reserved'
        : e === 'Disponible'
          ? t('reservation.repositionAvailable') || 'Available'
          : e;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size='xl'
      centered
      fullscreen='lg'
      className='reposition-modal'
      dialogClassName='reposition-modal-dialog'
    >
      <Modal.Header closeButton>
        <Modal.Title>{t('reservation.repositionTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='reposition-modal-body'>
        <p className='text-muted small mb-2 flex-shrink-0 reposition-modal-help'>
          {t('reservation.repositionHelp')}
        </p>
        <div className='reposition-modal-content position-relative d-flex flex-column flex-grow-1'>
          {swapBusy && (
            <div
              className='reposition-modal-saving d-flex align-items-center justify-content-center gap-2'
              aria-busy='true'
            >
              <Spinner animation='border' size='sm' role='status' />
              <span className='small text-muted'>{t('common.loading')}</span>
            </div>
          )}
          {loading ? (
            <div className='text-center py-4'>
              <Spinner animation='border' />
            </div>
          ) : categories.length === 0 ? (
            <p className='text-muted reposition-modal-empty'>
              {t('reservation.repositionNoCategories')}
            </p>
          ) : (
            <>
              <Nav
                variant='pills'
                className='flex-wrap gap-1 flex-shrink-0 reposition-modal-nav pb-2'
              >
                {categories.map(cat => (
                  <Nav.Item key={cat.nombre}>
                    <Nav.Link
                      active={activeCat === cat.nombre}
                      onClick={() => setActiveCat(cat.nombre)}
                    >
                      {cat.label || cat.nombre}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              {currentCat && (
                <div className='reposition-board-scroll'>
                  <div className='reposition-board-grid'>
                    {grid.map((row, rIdx) => (
                      <div key={rIdx} className='reposition-board-row'>
                        {row.map((casilla, cIdx) => (
                          <RepositionCell
                            key={casilla?._id || `empty-${rIdx}-${cIdx}`}
                            casilla={casilla}
                            cat={currentCat}
                            draggingId={draggingId}
                            setDraggingId={setDraggingId}
                            dropTargetId={dropTargetId}
                            setDropTargetId={setDropTargetId}
                            onSwap={onSwap}
                            estadoLabel={estadoLabel(casilla?.estado)}
                            t={t}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className='reposition-legend text-muted flex-shrink-0 pt-1 border-top'>
                <span>
                  <strong>V</strong> — {t('board.sold') || 'Sold'}
                </span>
                <span>
                  <strong>R</strong> — {t('board.reserved') || 'Reserved'}
                </span>
                <span>
                  <strong>·</strong> — {t('reservation.repositionAvailable') || 'Available'}
                </span>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
